import { z } from 'zod';

/**
 * Branded primitives.
 *
 * A bare `number` for latitude is indistinguishable from a `number` for
 * longitude, and swapping the two is the single most common geospatial bug —
 * it also happens to be the one PostGIS will silently accept, since
 * ST_MakePoint(lng, lat) takes them in the opposite order to how humans write
 * them. Branding makes the swap a compile error.
 */

/**
 * A phantom property rather than a `unique symbol`.
 *
 * `unique symbol` is the more common idiom, but it does not survive
 * cross-package declaration emit: any schema in another file that references a
 * branded type fails with TS4023 ("cannot be named"). A string-literal key is
 * portable, and brands remain mutually incompatible because the literal type
 * differs. The property never exists at runtime.
 */
export type Brand<T, B extends string> = T & { readonly __brand: B };

export type Latitude = Brand<number, 'Latitude'>;
export type Longitude = Brand<number, 'Longitude'>;
export type Meters = Brand<number, 'Meters'>;
export type MetersPerSecond = Brand<number, 'MetersPerSecond'>;
export type IsoDateTime = Brand<string, 'IsoDateTime'>;
export type IsoDate = Brand<string, 'IsoDate'>;

export const LatitudeSchema = z
  .number()
  .min(-90, 'Latitude must be between -90 and 90')
  .max(90, 'Latitude must be between -90 and 90')
  .refine(Number.isFinite, 'Latitude must be a finite number')
  .transform((v) => v as Latitude);

export const LongitudeSchema = z
  .number()
  .min(-180, 'Longitude must be between -180 and 180')
  .max(180, 'Longitude must be between -180 and 180')
  .refine(Number.isFinite, 'Longitude must be a finite number')
  .transform((v) => v as Longitude);

/** Horizontal accuracy in metres. Providers occasionally report absurd values. */
export const AccuracySchema = z
  .number()
  .nonnegative()
  .max(100_000, 'Accuracy value is implausible')
  .transform((v) => v as Meters);

export const AltitudeSchema = z.number().min(-500).max(12_000);

export const HeadingSchema = z.number().min(0).max(360);

/** Speed in m/s. 200 m/s = 720 km/h — beyond any ground vehicle. */
export const SpeedSchema = z
  .number()
  .nonnegative()
  .max(200)
  .transform((v) => v as MetersPerSecond);

export const BatteryLevelSchema = z.number().int().min(0).max(100);

/** ISO-8601 with an explicit offset. Naive local timestamps are always a bug here. */
export const IsoDateTimeSchema = z
  .string()
  .datetime({ offset: true })
  .transform((v) => v as IsoDateTime);

export const IsoDateSchema = z
  .string()
  .date()
  .transform((v) => v as IsoDate);

export const UuidSchema = z.string().uuid();

/** Positive database identifier. */
export const IdSchema = z.coerce.number().int().positive();

/**
 * A coordinate pair. Kept as one schema so latitude and longitude can never be
 * validated independently and then assembled in the wrong order.
 */
export const CoordinateSchema = z.object({
  latitude: LatitudeSchema,
  longitude: LongitudeSchema,
});
export type Coordinate = z.infer<typeof CoordinateSchema>;
